import { CommonModule } from '@angular/common'
import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  input,
  OnInit,
  Output,
  signal,
  ViewEncapsulation,
} from '@angular/core'
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { MatDividerModule } from '@angular/material/divider'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatSelectModule } from '@angular/material/select'
import { MatSnackBar } from '@angular/material/snack-bar'
import {
  EditWordPairDicArgs,
  EditWordPairDicCommand,
} from 'src/app/core/commands/edit-word-pair-dic/edit-word-pair-dic.command'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { CategoryService } from 'src/app/service/data/category.service'
import { DictionaryService } from 'src/app/service/data/dictionary.service'
import { TagService } from 'src/app/service/data/tag.service'
import { ThemeService } from 'src/app/service/theme/theme.service'
import { Article, ArticlesByLanguage, Languages, PartOfSpeech } from '../../enums/enums'
import { SelectOption } from '../../interfaces/word'
import { Category } from '../../models/main/category'
import { Dictionary } from '../../models/main/dictionary'
import { Pair } from '../../models/main/pair'
import { Tag } from '../../models/main/tag'
import { Word } from '../../models/main/word'
import { DdcAddMobileSheetComponent } from '../ddc-add-mobile-sheet/ddc-add-mobile-sheet.component'
import { DdcButtonSOneComponent } from '../ddc-button-s-one/ddc-button-s-one.component'
import { DdcElemChooserOneComponent } from '../ddc-elem-chooser-one/ddc-elem-chooser-one.component'
import { DdcPrivacyComponent } from '../ddc-privacy/ddc-privacy.component'
import { DdcTextGradientComponent } from '../ddc-text-gradient/ddc-text-gradient.component'

@Component({
  selector: 'ddc-edit-mobile-sheet',
  templateUrl: './ddc-edit-mobile-sheet.component.html',
  styleUrl: './ddc-edit-mobile-sheet.component.scss',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    AngularMaterialsModules,
    CommonModule,
    MatIconModule,
    FormsModule,
    MatDividerModule,
    DdcPrivacyComponent,
    DdcTextGradientComponent,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatDialogModule,
    DdcButtonSOneComponent,
    DdcElemChooserOneComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DdcEditMobileSheetComponent implements OnInit {
  word = input<Word>(undefined)
  pair = input<Pair>(undefined)
  dictionary = input<Dictionary>(undefined)
  collection = input<{ id: number; name: string }>(undefined)
  @Output() edit = new EventEmitter<Word>()
  @Output() cancel = new EventEmitter<void>()

  protected EditWordPairDicCommand: EditWordPairDicCommand
  _form: FormGroup

  private formValues = signal<any>(null)
  protected selectedTags = signal<Tag[]>([])
  protected selectedTags2 = signal<Tag[]>([])
  protected selectedCategory = signal<Category[]>([])
  protected selectedCategory2 = signal<Category[]>([])
  protected selectedDics = signal<{ id: number; label: string }[]>([])

  _isDialogLoading = false
  _isDarkTheme = false
  _isLightTheme = true
  _isPaperTheme = false
  _wordCreated = false
  _pairCreated = false
  _hasDic = false
  _targetDicTitle = ''
  _addError = ''
  _isSubscriptionError = false
  _addedField = ''
  _addedField2 = ''

  _selectedLanguage = ''
  _selectedLanguage2 = ''

  _selectedArticle = ''
  _selectedArticle2 = ''

  _selectedPos = ''
  _selectedPos2 = ''

  _selectedLngHasArticle = false
  _selectedLngHasArticle2 = false

  _wordMaxlength = 100
  _definitionMaxlength = 500
  _exampleMaxlength = 200

  _loadedCats: boolean = false
  _loadedTags: boolean = false
  _loadedDics: boolean = false

  _userCats: any[] = []
  _userTags: any[] = []
  _userDics: any[] = []
  _isCreateBtnLoading = false
  _submitBtnText = 'Edit'
  _selectedPrivacyChipOptions: string = ''
  _selectedChipOptions: string[] = ['word']

  languages: SelectOption[] = []
  articles: SelectOption[] = []
  poses: SelectOption[] = []

  languages2: SelectOption[] = []
  articles2: SelectOption[] = []
  poses2: SelectOption[] = []

  private commandArgs = computed(() => {
    const value = this.formValues()
    if (!value) return null

    const id: number =
      this.word()?.id || this.pair()?.id || this.dictionary()?.id || this.collection()?.id || 0

    const args: EditWordPairDicArgs = {
      id: id,
      word1: {
        language: value._1_language,
        article: value._1_article,
        word: value._1_word,
        pos: value._1_pos,
        definition: value._1_definition,
        example: value._1_example,
        category: this.selectedCategory()?.length > 0 ? this.selectedCategory()[0] : undefined,
        tags: this.selectedTags(),
      },
      privacy: value._privacy,
      word2: undefined,
      dictionaries: this.selectedDics(),
    }

    // Add word2 data if it's a pair
    if (this.pair() !== undefined) {
      args.word2 = {
        language: value._2_language,
        article: value._2_article,
        word: value._2_word,
        pos: value._2_pos,
        definition: value._2_definition,
        example: value._2_example,
        category: this.selectedCategory2()?.length > 0 ? this.selectedCategory2()[0] : undefined,
        tags: this.selectedTags2(),
      }
    }

    // Add dictionary data if creating a dictionary
    if (this.dictionary() !== undefined) {
      args.word1 = undefined
      args.word2 = undefined
      args.privacy = undefined
      args.dictionary_title = value._dictionary_title
      args.dictionary_description = value._dictionary_description
    }

    // Add collection data if creating a collection
    if (this.collection() !== undefined) {
      args.word1 = undefined
      args.word2 = undefined
      args.privacy = undefined
      args.dictionary_title = undefined
      args.dictionary_description = undefined
      args.collection_name = value._collection_name
    }

    return args
  })

  constructor(
    private _fb: FormBuilder,
    private _themeService: ThemeService,
    private _snackBar: MatSnackBar,
    private cat_service: CategoryService,
    private tag_service: TagService,
    private dic_service: DictionaryService,
    private _dialogRef: MatDialogRef<DdcAddMobileSheetComponent>,
    editWordPairDicCmd: EditWordPairDicCommand,
  ) {
    this.EditWordPairDicCommand = editWordPairDicCmd
    this._form = this._fb.group({
      _type: ['word'],

      _1_language: ['', Validators.required],
      _1_article: [''],
      _1_word: ['', [Validators.required, Validators.maxLength(this._wordMaxlength)]],
      _1_pos: [''],
      _1_definition: ['', [Validators.maxLength(this._definitionMaxlength)]],
      _1_example: ['', [Validators.maxLength(this._exampleMaxlength)]],

      _2_language: ['', Validators.required],
      _2_article: [''],
      _2_word: ['', [Validators.required, Validators.maxLength(this._wordMaxlength)]],
      _2_pos: [''],
      _2_definition: ['', [Validators.maxLength(this._definitionMaxlength)]],
      _2_example: ['', [Validators.maxLength(this._exampleMaxlength)]],

      _privacy: ['P01'],

      _dictionary_title: [
        '',
        [Validators.minLength(1), Validators.maxLength(this._exampleMaxlength)],
      ],
      _dictionary_description: [
        '',
        [Validators.minLength(1), Validators.maxLength(this._exampleMaxlength)],
      ],
      _collection_name: [
        '',
        [Validators.minLength(1), Validators.maxLength(this._exampleMaxlength)],
      ],
    })

    this._themeService.theme$.subscribe(theme => {
      this._isDarkTheme = theme === 'dark-theme'
      this._isLightTheme = theme === 'light-theme'
      this._isPaperTheme = theme === 'paper-theme'
    })
  }

  ngOnInit(): void {
    this._selectedPrivacyChipOptions = 'P01'
    this.getLanguages()
    this.getLanguages2()

    this.getArticles()
    this.getArticles2()

    this.getPos()
    this.getPos2()

    this.getUserTags()
    this.getUserCats()
    this.getUserDics()

    // Initialize form values signal
    this.formValues.set(this._form.value)

    // Initialize form values from word input
    if (this.word() !== undefined) {
      this._form.patchValue({
        _type: 'word',
        _1_language: this.word()?.language,
        _1_article: this.word()?.article,
        _1_word: this.word()?.word,
        _1_pos: this.word()?.pos,
        _1_definition: this.word()?.definition,
        _1_example: this.word()?.example,
        _privacy: this.word()?.privacy,
      })
      this._selectedLanguage = this.word()?.language || ''
      this.onLanguageChange({ value: this._selectedLanguage })
      //category and tags
      if (
        this.word()?.category &&
        this.word()?.category.label &&
        this.word()?.category.label.length !== 0
      ) {
        this.selectedCategory.set([this.word()?.category])
      }
      if (this.word()?.tags && this.word()?.tags.length !== 0) {
        this.selectedTags.set(this.word()?.tags)
      }
      //dictionaries
      if (this.word()?.dictionaries && this.word()?.dictionaries.length !== 0) {
        this.selectedDics.set(
          this.word()?.dictionaries.map(dic => ({
            id: dic.id,
            label: dic.title,
          })),
        )
      }
    }
    // Initialize form values from pair input
    if (this.pair() !== undefined) {
      this._form.patchValue({
        _type: 'pair',
        // Word 1
        _1_language: this.pair()?.word_1.language,
        _1_article: this.pair()?.word_1.article,
        _1_word: this.pair()?.word_1.word,
        _1_pos: this.pair()?.word_1.pos,
        _1_definition: this.pair()?.word_1.definition,
        _1_example: this.pair()?.word_1.example,
        // Word 2
        _2_language: this.pair()?.word_2.language,
        _2_article: this.pair()?.word_2.article,
        _2_word: this.pair()?.word_2.word,
        _2_pos: this.pair()?.word_2.pos,
        _2_definition: this.pair()?.word_2.definition,
        _2_example: this.pair()?.word_2.example,
        // Privacy and dictionaries
        _privacy: this.pair()?.word_1.privacy,
      })
      this._selectedLanguage = this.pair()?.word_1.language || ''
      this.onLanguageChange({ value: this._selectedLanguage })
      //category and tags w_1
      if (
        this.pair()?.word_1.category &&
        this.pair()?.word_1.category.label &&
        this.pair()?.word_1.category.label.length !== 0
      ) {
        this.selectedCategory.set([this.pair()?.word_1.category])
      }
      if (this.pair()?.word_1.tags && this.pair()?.word_1.tags.length !== 0) {
        this.selectedTags.set(this.pair()?.word_1.tags)
      }
      //category and tags w_2
      if (
        this.pair()?.word_2.category &&
        this.pair()?.word_2.category.label &&
        this.pair()?.word_2.category.label.length !== 0
      ) {
        this.selectedCategory2.set([this.pair()?.word_2.category])
      }
      if (this.pair()?.word_2.tags && this.pair()?.word_2.tags.length !== 0) {
        this.selectedTags2.set(this.pair()?.word_2.tags)
      }
      //dictionaries
      if (this.pair()?.dictionaries && this.pair()?.dictionaries.length !== 0) {
        this.selectedDics.set(
          this.pair()?.dictionaries.map(dic => ({
            id: dic.id,
            label: dic.title,
          })),
        )
      }
    }

    if (this.dictionary() !== undefined) {
      this._form.patchValue({
        _type: 'dictionary',
        _dictionary_title: this.dictionary().title,
        _dictionary_description: this.dictionary().description,
      })
    }

    if (this.collection() !== undefined) {
      this._form.patchValue({
        _type: 'collection',
        _collection_name: this.collection().name,
      })
    }
    // Subscribe to form value changes
    this._form.valueChanges.subscribe(value => {
      this.formValues.set(value)
      const args = this.commandArgs()
      if (args) {
        this.EditWordPairDicCommand.setArgs(args)
      }
    })

    Promise.all([this.getLanguages(), this.getArticles(), this.getPos()]).then(() => {
      setTimeout(() => {
        this._isDialogLoading = false
      }, 500)
    })
  }

  isFormValid(): boolean {
    if (this.word() !== undefined) {
      const lng = this._form.get('_1_language').valid
      const art = this._form.get('_1_article').valid
      const word = this._form.get('_1_word').valid
      const pos = this._form.get('_1_pos').valid
      const exp = this._form.get('_1_example').valid
      const def = this._form.get('_1_definition').valid

      if (lng && art && word && pos && exp && def) return true
    } else if (this.pair() !== undefined) {
      const lng = this._form.get('_1_language').valid
      const art = this._form.get('_1_article').valid
      const word = this._form.get('_1_word').valid
      const pos = this._form.get('_1_pos').valid
      const exp = this._form.get('_1_example').valid
      const def = this._form.get('_1_definition').valid

      const lng2 = this._form.get('_2_language').valid
      const art2 = this._form.get('_2_article').valid
      const word2 = this._form.get('_2_word').valid
      const pos2 = this._form.get('_2_pos').valid
      const exp2 = this._form.get('_2_example').valid
      const def2 = this._form.get('_2_definition').valid
      if (
        lng &&
        art &&
        word &&
        pos &&
        exp &&
        def &&
        lng2 &&
        art2 &&
        word2 &&
        pos2 &&
        exp2 &&
        def2
      ) {
        return true
      }
    } else if (this.dictionary() !== undefined) {
      const dic_title = this._form.get('_dictionary_title')
      const dic_description = this._form.get('_dictionary_description')
      if (
        dic_title &&
        dic_description &&
        dic_title.valid &&
        dic_description.valid &&
        dic_title.value &&
        dic_description.value &&
        dic_title.value.length > 0 &&
        dic_description.value.length > 0
      )
        return true
    } else if (this.collection() !== undefined) {
      const coll_name = this._form.get('_collection_name')
      if (coll_name && coll_name.valid && coll_name.value && coll_name.value.length > 0) return true
    }

    return false
  }

  getLanguages(): void {
    this.languages = Object.entries(Languages).map(([key, [viewValue, rawValue]]) => ({
      raw_value: rawValue,
      view_value: viewValue,
    }))
  }

  getLanguages2(): void {
    this.languages2 = Object.entries(Languages).map(([key, [viewValue, rawValue]]) => ({
      raw_value: rawValue,
      view_value: viewValue,
    }))
  }
  getArticles(): void {
    const articlesArray = Object.values(Article) as string[]

    this.articles.push({
      raw_value: null,
      view_value: 'None',
    })

    // Loop through the enum values and create SelectOption objects
    for (const value of articlesArray) {
      this.articles.push({
        raw_value: value,
        view_value: value,
      })
    }
  }

  getUserTags(): void {
    this.tag_service.get_tags().subscribe((res: any) => {
      if (res) {
        this._userTags = res
        this._loadedTags = true
      }
    })
  }

  getUserCats(): void {
    this.cat_service.get_cats().subscribe((res: any) => {
      if (res) {
        this._userCats = res
        this._loadedCats = true
      }
    })
  }

  getUserDics(): void {
    this.dic_service.get_dics(true).subscribe({
      next: (res: any) => {
        if (res) {
          // Map the response to transform 'title' to 'label'
          this._userDics = res.map((dic: { id: number; title: string }) => ({
            id: dic.id,
            label: dic.title,
          }))
          this._loadedDics = true
        } else {
        }
      },
      error: (error: any) => {
        this._isSubscriptionError = true
      },
    })
  }

  getArticles2(): void {
    const articlesArray = Object.values(Article) as string[]

    this.articles2.push({
      raw_value: null,
      view_value: 'None',
    })

    // Loop through the enum values and create SelectOption objects
    for (const value of articlesArray) {
      this.articles2.push({
        raw_value: value,
        view_value: value,
      })
    }
  }

  getPos(): void {
    const posArray = Object.values(PartOfSpeech) as string[]

    this.poses.push({
      raw_value: null,
      view_value: 'None',
    })

    // Loop through the enum values and create SelectOption objects
    for (const value of posArray) {
      this.poses.push({
        raw_value: value,
        view_value: value,
      })
    }
  }

  getPos2(): void {
    const posArray = Object.values(PartOfSpeech) as string[]

    this.poses2.push({
      raw_value: null,
      view_value: 'None',
    })

    // Loop through the enum values and create SelectOption objects
    for (const value of posArray) {
      this.poses2.push({
        raw_value: value,
        view_value: value,
      })
    }
  }

  onLanguageChange(event: any): void {
    const selectedLng = event.value

    this.articles = []
    this.articles.push({
      raw_value: null,
      view_value: 'None',
    })

    const articlesArray = ArticlesByLanguage[selectedLng]

    if (articlesArray !== undefined && articlesArray.length > 0) {
      this._selectedLngHasArticle = true
      for (const value of articlesArray) {
        this.articles.push({
          raw_value: value,
          view_value: value,
        })
      }
    } else {
      this._selectedLngHasArticle = false
      this._selectedArticle = null
    }
  }

  onLanguageChange2(event: any): void {
    const selectedLng2 = event.value

    this.articles2 = []
    this.articles2.push({
      raw_value: null,
      view_value: 'None',
    })

    const articlesArray2 = ArticlesByLanguage[selectedLng2]

    if (articlesArray2 !== undefined && articlesArray2.length > 0) {
      this._selectedLngHasArticle2 = true
      for (const value of articlesArray2) {
        this.articles2.push({
          raw_value: value,
          view_value: value,
        })
      }
    } else {
      this._selectedLngHasArticle2 = false
    }
  }

  updateSelectedTags(tags: any): void {
    this.selectedTags.set(tags)
    this.updateCommandArgsFromSignals()
  }

  updateSelectedTags2(tags: any): void {
    this.selectedTags2.set(tags)
    this.updateCommandArgsFromSignals()
  }

  updateSelectedCat(cat: any): void {
    this.selectedCategory.set(cat)
    this.updateCommandArgsFromSignals()
  }

  updateSelectedCat2(cat: any): void {
    this.selectedCategory2.set(cat)
    this.updateCommandArgsFromSignals()
  }

  updateSelectedDics(dictionaries: any): void {
    this.selectedDics.set(dictionaries)
    this.updateCommandArgsFromSignals()
  }

  updateArgs(): void {
    if (this._form.valid) {
      this.formValues.set(this._form.value)
      const args = this.commandArgs()
      if (args) {
        this.EditWordPairDicCommand.setArgs(args)
      }
    }
  }

  private updateCommandArgsFromSignals(): void {
    this.EditWordPairDicCommand.setArgs(this.commandArgs())
  }

  add(): void {
    if (this._form.valid) {
      this.updateArgs()
      this._isCreateBtnLoading = true

      this.EditWordPairDicCommand.execute().subscribe(success => {
        this._isCreateBtnLoading = false
        if (success) {
          this._wordCreated = true
          this._addedField = this._form.get('_1_word')?.value
        } else {
          this._addError = 'Error creating entry'
          this._snackBar.open(this._addError, 'Close', {
            duration: 3000,
          })
        }
      })
    } else {
      this._snackBar.open('Please fill in all required fields', 'Close', {
        duration: 3000,
      })
    }
  }

  closeDialog(): void {
    this._dialogRef.close()
  }
}
